import { unwrap } from "@sagot/api-client";
import { SETUP_STORAGE_KEY } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useErrorMessage } from "../../../lib/api-errors";
import { useApi } from "../../../session/session-provider";
import { useInvalidateBilling } from "./use-billing";

export type HostedGateway = "stripe" | "paypal";

interface PendingSetup {
  gateway: HostedGateway;
  setupId: string;
}

/** Mock gateways (development) answer with a non-routable `.invalid` approval page. */
export function isMockApproval(url: string): boolean {
  try {
    return new URL(url).hostname.endsWith(".invalid");
  } catch {
    return false;
  }
}

export function rememberSetup(pending: PendingSetup): void {
  try {
    window.sessionStorage.setItem(SETUP_STORAGE_KEY, JSON.stringify(pending));
  } catch {
    // Storage blocked (private mode): the return page asks the user to start again.
  }
}

/** The setup started before the gateway redirect, if it matches the returning gateway. */
export function takeSetup(gateway: string | null): PendingSetup | null {
  try {
    const raw = window.sessionStorage.getItem(SETUP_STORAGE_KEY);
    window.sessionStorage.removeItem(SETUP_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== "object") return null;
    const { gateway: saved, setupId } = parsed as Partial<PendingSetup>;
    return (saved === "stripe" || saved === "paypal") && typeof setupId === "string" && saved === gateway ? { gateway: saved, setupId } : null;
  } catch {
    return null;
  }
}

/** Finish a saved-method setup (Stripe SetupIntent / PayPal vault token) and refresh billing. */
export function useCompleteSetup() {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: (pending: PendingSetup) =>
      unwrap(api.POST("/v1/billing/payment-methods/setup/complete", { body: { gateway: pending.gateway, setup_id: pending.setupId } })),
    onSuccess: async () => {
      toast.success(t("billing.methods.saved"));
      await invalidate();
    },
    onError: (caught) => toast.error(message(caught)),
  });
}

/** "Add PayPal" (or a card on Stripe when that gateway is offered): hosted setup page, then back to Billing. `onDone` runs once the account is saved (mock gateway) or when the browser leaves for the gateway. */
export function useStartSetup(onDone?: () => void) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const complete = useCompleteSetup();
  return useMutation({
    mutationFn: (gateway: HostedGateway) => unwrap(api.POST("/v1/billing/payment-methods/setup", { body: { gateway } })),
    onSuccess: (session, gateway) => {
      const pending = { gateway, setupId: session.setup_id };
      if (isMockApproval(session.approve_url)) {
        complete.mutate(pending, { onSuccess: onDone });
        return;
      }
      rememberSetup(pending);
      toast.info(t("billing.methods.setupRedirect"));
      onDone?.();
      window.location.assign(session.approve_url);
    },
    onError: (caught) => toast.error(message(caught)),
  });
}
